import React, { useState, useEffect, useLayoutEffect } from 'react'
import { useAppSelector, useAppDispatch } from '../hooks'
import { setUserData, setPlayerName } from '../stores/UserStore'
import type { UserData } from '../types/user'
import { ServiceFactory } from '../core/factories/ServiceFactory'
import ProfileEditDialog from './ProfileEditDialog'
import { trackEvent } from '../services/analyticsService'
import { normalizeRoutePath } from '../utils/routeUtils'
import { getDisplayName } from '../utils/personName'

interface FirstTimeProfileSetupProps {
  onComplete?: () => void
}

const FirstTimeProfileSetup: React.FC<FirstTimeProfileSetupProps> = ({ onComplete }) => {
  const userData = useAppSelector((state) => state.user.userData)
  const dispatch = useAppDispatch()
  const [showProfileDialog, setShowProfileDialog] = useState(false)
  const [isChecking, setIsChecking] = useState(true)

  useLayoutEffect(() => {
    if (!userData?.id || userData.emailVerified !== false) return
    if (typeof window === 'undefined') return
    if (normalizeRoutePath(window.location.pathname) === '/verify-email') return
    window.location.replace('/verify-email')
  }, [userData?.id, userData?.emailVerified])

  useEffect(() => {
    const checkFirstTimeSetup = async () => {
      if (!userData) {
        setIsChecking(false)
        return
      }

      if (userData.emailVerified === false) {
        setShowProfileDialog(false)
        setIsChecking(false)
        return
      }

      try {
        const authService = ServiceFactory.getInstance().getAuthService()
        const profile = await authService.getProfile(userData.id)
        
        // Check if this is a new user who hasn't completed their profile
        const isNewUser = !profile?.profileCompleted && (!profile?.firstName || !profile?.lastName)
        
        if (isNewUser) {
          console.log('[FirstTimeProfileSetup] New user detected, showing profile dialog')
          trackEvent('APP_onboarding_started', { onboarding_trigger: 'new_user' })
          setShowProfileDialog(true)
        } else {
          console.log('[FirstTimeProfileSetup] User has completed profile setup')
          onComplete?.()
        }
      } catch (error) {
        console.error('[FirstTimeProfileSetup] Error checking profile:', error)
        // If there's an error, assume user needs to complete profile
        trackEvent('APP_onboarding_started', { onboarding_trigger: 'profile_check_error' })
        setShowProfileDialog(true)
      } finally {
        setIsChecking(false)
      }
    }

    checkFirstTimeSetup()
  }, [userData, onComplete])

  const handleProfileComplete = async (updatedUserData?: UserData) => {
    try {
      // Mark profile as completed in Back4App
      const authService = ServiceFactory.getInstance().getAuthService()
      await authService.updateProfile(userData!.id, {
        profileCompleted: true
      })

      // Update local user data with the updated profile data
      const finalUserData: UserData = updatedUserData || {
        ...userData!,
        profileCompleted: true
      }
      dispatch(setUserData(finalUserData))

      // Update player name mapping for immediate UI updates
      const displayName = getDisplayName(finalUserData)
      dispatch(setPlayerName({ id: userData!.id, name: displayName }))

      // Player name service removed - using centralized nameplate system instead

      console.log('[FirstTimeProfileSetup] Profile setup completed')
      trackEvent('APP_onboarding_completed')
      setShowProfileDialog(false)
      onComplete?.()
    } catch (error) {
      console.error('[FirstTimeProfileSetup] Error marking profile as completed:', error)
    }
  }

  const handleCloseDialog = () => {
    // For first-time setup, we don't allow closing without completing
    // The ProfileEditDialog will handle the validation
  }

  if (isChecking) {
    return null // Don't render anything while checking
  }

  return (
    <>
      {showProfileDialog && (
        <ProfileEditDialog 
          open={showProfileDialog} 
          onClose={handleCloseDialog}
          forceRequired={true} // Force completion for first-time users
          onProfileComplete={handleProfileComplete}
        />
      )}
    </>
  )
}

export default FirstTimeProfileSetup 